
import { Component, Vue, Prop, Emit, Watch } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

import { get } from "lodash"

import "@/scripts/array-operations"
import { caseColumns, CaseColumn } from "@/types/caseColumns"

import { Case, Field, DataType, CaseDetails, dataTypeByFieldType, CalculationDataTypeValue } from "@/types"
import { Team } from "@/types/team"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import moment from "moment"

import SkeletonLoader from "../SkeletonLoader.vue"
import CaseTable from "@/components/CaseList/CaseTable.vue"

import { View } from "@/types/views/view"
import { Severity } from "@/types/severity"
import { groupingFunctions } from "@/types/dataType"

import { formatDateMixin } from "@/mixins/formatDateMixin"
import { CalculationType } from "@/types/field"

import UserImage from "@/components/UserImage.vue"
import { UserSettings } from "@/types/userSettings"
import { Schedule } from "@/types/schedule"
import { CurrentPageDetails } from "@/types/paginatedRequest"
import { ViewsService } from "@/services/viewsService"
import { CasesTable } from "@/types/case"

const caseStore = namespace("cases")
const setupStore = namespace("setup")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"skeleton": SkeletonLoader,
		"case-table": CaseTable,
		"user-image": UserImage,
		"schedule-popup": () => import("@/components/Config/Schedules/ScheduleInstance.vue")
	}
})
export default class CaseList extends Vue {
	@Prop({ type: String, default: "" })
	filter!: string;

	@Prop()
	cases!: Case[] | null;

	@Prop()
	casesTables!: CasesTable[] | null;

	@Prop()
	activeView!: View | null;

	@Prop({ type: Boolean, default: true })
	enableFiltering!: boolean;

	@Prop({ type: Number, default: 20 })
	perPage!: number;

	@Prop({ type: Number, default: 0 })
	totalRecords!: number

	@Prop({ default: null, required: false })
	groupValue!: string | null

	@Prop({ default: () => [] })
	loadingPageTableIds!: string[];

	@caseStore.State severities!: Severity[];
	@caseStore.State viewsService!: ViewsService;

	@Getter("getScreenWidth")
	public screenWidth!: number;

	@Getter("getTeamsById")
	public teamsById!: { [key: string]: Team };

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	@Getter("getUserSettings")
	public userSettings!: UserSettings;

	@Getter("getUseV2")
	private readonly shouldUseNewInterface!: boolean;

	@setupStore.State fields!: Field[];
	@setupStore.Action loadFieldsIfUnloaded!: () => Promise<void>;

	public fieldsDictionary: any = [];

	private searchFilter: string = "";
	private viewScheduleInstance: {
		schedule: Schedule,
		date: Date
	} | null = null

	private groupPages: { [key: string]: CurrentPageDetails } = {};
	private debounceSearch: NodeJS.Timeout | null = null;
	private defaultColumnSort: string = "";
	private defaultSortDataType: DataType = DataType.none;
	private loadingPage: boolean = false;

	async mounted () {
		await this.loadFieldsIfUnloaded()
		this.buildFieldsDictionary()
		this.setupTablePages()
	}

	private setupTablePages (): void {
		if (!!this.casesTables && this.casesTables?.length > 0) {
			this.groupPages = this.casesTables.reduce((acc, table) => {
				table.value = table.value ?? "none"

				acc[table.value] = {
					value: table.value,
					pageNumber: 1,
					sortBy: this.defaultColumnSort,
					sortDesc: false
				}
				return acc
			}, {} as { [key: string]: CurrentPageDetails })
		}
	}

	private buildFieldsDictionary (): void {
		// Build fields dictionary for quick access using case columns and custom fields
		caseColumns.forEach((field: CaseColumn) => {
			this.fieldsDictionary[field.key] = { title: field.label, dataType: field.dataType, displayKey: field.displayKey, group: field.group }
		})
		this.fields.forEach((field: any) => {
			this.fieldsDictionary["fields." + field.id] = {
				title: field.title,
				dataType: field.dataType,
				displayKey: field.dataType === "user-role" ? null : "fields." + field.id + ".label",
				fieldDataType: field.dataType === "calculated"
					? CalculationDataTypeValue[field.extensions!.calculationType as CalculationType]
					: dataTypeByFieldType[field.dataType!]
						? dataTypeByFieldType[field.dataType!]
						: null
			}
		})
	}

	public get columns (): any[] {
		if (!this.activeView) {
			return [
				{
					key: "caseNumber",
					label: "Case Number"
				},
				{
					key: "title",
					label: "Title"
				},
				{
					key: "templateTitle",
					label: "Template"
				},
				{
					key: "assigneeId",
					label: "Assignee",
					dataType: "user"
				}
			]
		}

		return this.activeView.columns.map(c => {
			if (c.startsWith("fields.")) {
				let fieldId = c.substring(7)

				if (fieldId.endsWith(".label")) {
					fieldId = fieldId.substring(0, fieldId.length - 6)
				}

				const fieldColumn = this.fields.find(field => field.id === fieldId)
				if (fieldColumn) {
					return {
						key: c,
						fieldId: fieldColumn.id,
						label: fieldColumn.title,
						dataType: fieldColumn.dataType,
						fieldDataType: fieldColumn.dataType === "calculated"
							? CalculationDataTypeValue[fieldColumn.extensions!.calculationType as CalculationType]
							: dataTypeByFieldType[fieldColumn.dataType!]
								? dataTypeByFieldType[fieldColumn.dataType!]
								: null,
						extensions: fieldColumn.extensions,
						dynamicField: true,
						listLabel: "label"
					}
				}
			} else {
				const caseColumn = caseColumns.find(column => column.key === c)
				if (caseColumn) {
					return {
						key: caseColumn.key,
						displayKey: caseColumn.displayKey,
						label: caseColumn.label,
						dataType: caseColumn.dataType,
						fieldDataType: caseColumn.fieldDataType,
						rounding: caseColumn.rounding,
						listLabel: caseColumn.listLabel || "title",
						listItemType: caseColumn.listItemType
					}
				}
			}

			return null
		}).filter(c => !!c)
	}

	public get checkRecordsAvailable (): boolean {
		return this.casesTables?.some((c: CasesTable) => c.totalRecords > 0) ?? false
	}

	public get groupedCases (): any[] {
		if (!this.loadingPage) {
			this.buildFieldsDictionary()
		}

		let cases = this.cases!

		if (this.activeView && this.activeView.groupBy) {
			if (!this.fieldsDictionary[this.activeView!.groupBy]) {
				this.activeView.groupBy = null
			}
		}

		if (this.activeView && this.activeView.groupBy) {
			const groupByKeyColumn = this.fieldsDictionary[this.activeView!.groupBy]
			const groupBy = groupByKeyColumn && groupByKeyColumn.displayKey
				? groupByKeyColumn.displayKey
				: this.activeView!.groupBy

			const caseUsers: { [key:string]: any } = {}
			let getGroups: (record: any, groupByValue: any, cache: { [key:string]: any }, groupByOption?: string | null) => { key: string, label: string, value: any }[] =
				groupByKeyColumn && groupByKeyColumn.group
					? groupByKeyColumn.group
					: groupByKeyColumn && groupingFunctions.hasOwnProperty(groupByKeyColumn.fieldDataType)
						? get(groupingFunctions, groupByKeyColumn.fieldDataType)
						: (record: any, groupByValue: any, cache, groupByOption: string) => {
							return [
								{
									key: groupByValue || "not set",
									label: groupByValue || "Not Set",
									value: groupByValue
								}
							]
						}

			const groupLabels: { [key:string]: {
					label: string,
					value: any,
					sortValue?: any
				}
			} = {}

			if (!!cases && cases.length > 0) {
				const caseGroups = cases
					.reduce((group: any, caseItem: any) => {
						let groupKey = get(caseItem, groupBy)
						if (!groupKey) {
							groupKey = get(caseItem, this.activeView!.groupBy!)
							if (groupKey && groupKey.value !== undefined) {
								groupKey = groupKey.value
							}
						}

						if (this.activeView && this.activeView.groupByDataType === DataType.dateTime) {
							const value = groupKey
							groupKey = groupKey
								? moment(groupKey).format("L")
								: "Not set"
							groupLabels[groupKey] = {
								label: groupKey,
								value,
								sortValue: value
							};
							(group[groupKey] = group[groupKey] || []).push(caseItem)
						} else {
							const itemGroups = getGroups(caseItem, groupKey, caseUsers, this.activeView!.groupByOption)
							itemGroups.forEach(itemGroup => {
								groupLabels[itemGroup.key] = {
									label: itemGroup.label,
									value: itemGroup.value
								};
								(group[itemGroup.key] = group[itemGroup.key] || []).push(caseItem)
							})
						}

						return group
					}, {})
			}

			if (this.casesTables && this.casesTables.length > 0) {
				this.casesTables.forEach((table: CasesTable) => {
					const caseItem = table.cases[0]
					let groupKey = get(caseItem, groupBy)
					const itemGroups = getGroups(table.cases[0], groupKey, caseUsers, this.activeView!.groupByOption)
					itemGroups.forEach(itemGroup => {
						if (this.activeView && this.activeView.groupByDataType === DataType.dateTime) {
							if (this.activeView.groupBy?.includes("fields")) {
								const label = table.value !== "none" ? table.value : "Not set"
								groupLabels[table.value] = {
									label,
									value: itemGroup.value
								}
							} else {
								groupKey = groupKey
									? moment(groupKey).format("L")
									: "Not set"
								groupLabels[table.value] = {
									label: groupKey,
									value: itemGroup.value
								}
							}
						} else if (this.activeView && this.activeView.groupByDataType === DataType.boolean) {
							groupLabels[table.value] = {
								label: table.value,
								value: table.value
							}
						} else {
							groupLabels[table.value] = {
								label: itemGroup.label,
								value: itemGroup.value
							}
						}
					})
				})
			}

			const res = this.casesTables?.map((table: CasesTable) => {
				return Vue.observable({
					key: table.value,
					label: groupLabels[table.value] ? groupLabels[table.value].label : table.value,
					value: groupLabels[table.value] ? groupLabels[table.value].value : table.value,
					sort: this.activeView!.groupBy === "severityId"
						? this.severityLevel(table.value)
						: groupLabels[table.value]
							? groupLabels[table.value].sortValue
								? groupLabels[table.value].sortValue
								: groupLabels[table.value].label
							: table.value,
					cases: table.cases,
					totalRecords: table.totalRecords,
					currentPage: this.groupPages[table.value]?.pageNumber ?? 1
				})
			})

			this.loadingPage = false
			return res ?? []
		} else {
			// We expect this to be a single table
			if (this.casesTables?.length === 1) {
				const table: CasesTable = this.casesTables[0]

				this.loadingPage = false
				return [Vue.observable({
					key: table.value,
					label: null,
					cases: table.cases,
					currentPage: this.groupPages[table.value]?.pageNumber ?? 1,
					value: table.value,
					sortBy: this.defaultColumnSort,
					sortDesc: false,
					totalRecords: table.totalRecords
				})]
			}

			this.loadingPage = false
			return []
		}
	}
	private severityLevel (severityTitle: string): number {
		const severity = this.severities && this.severities.find(p => p.title === severityTitle)
		if (severity) {
			return 9999 - severity.level
		} else {
			return 0
		}
	}

	// Method to display checkbox value as string dependent on its value
	private displayCheckBoxValue (value: string): string {
		switch (value.toLowerCase()) {
			case "null":
			case "false":
			case "no":
				return "No"
			case "undefined":
				return "Not Specified"
			default:
				return "Yes"
		}
	}

	private newCase (): void {
		this.$router.push({ path: "/cases/New" })
	}

	private getFieldLabel (fieldId: string, caseDetails: CaseDetails): string | null {
		if (!caseDetails.fields || !caseDetails.fields[fieldId]) {
			return null
		}

		return caseDetails.fields[fieldId].label
	}

	private get caseFilteringTotal (): string {
		const casesTotal = this.totalRecordsAvailable()
		const casesShown = this.totalRecordsShown()
		const record: string = "record"
		const records: string = "records"

		if (this.casesTables && this.casesTables.length > 0 && this.searchFilter && /\S/.test(this.searchFilter)) {
			const recordString = casesTotal === 1 ? record : records
			return `Showing ${casesShown} of ${casesTotal} ${recordString}`
		} else if (this.cases) {
			const recordString = casesShown === 1 ? record : records
			return `${casesShown} ${recordString}`
		} else {
			return ""
		}
	}

	forecastClicked (caseItem: { scheduling: any }) {
		console.log("CaseList", "forecastClicked")
		if (!caseItem.scheduling) return

		this.viewScheduleInstance = {
			schedule: {
				id: caseItem.scheduling.scheduleId,
				title: caseItem.scheduling.scheduleTitle
			} as Schedule,
			date: new Date(caseItem.scheduling.instanceDate)
		}

		console.log("CaseList", this.viewScheduleInstance)
	}

	scheduleUpdated (schedule: Schedule) {
	}

	@Watch("viewsService.activeViewId", { deep: true })
	private onActiveViewIdChanged (newValue: string, oldValue: string) {
		if (newValue !== oldValue) {
			this.searchFilter = ""

			this.groupPages = {}
			if (!!this.casesTables && this.casesTables?.length > 0) {
				this.casesTables.forEach((table: CasesTable) => {
					this.groupPages[table.value] = {
						value: table.value,
						pageNumber: 1,
						sortBy: this.defaultColumnSort,
						sortDesc: false
					}
				})
			}
		}
	}

	private onPageClick (group: any): void {
		if (!this.loadingPage) {
			this.loadingPage = true

			// Don't trigger a page change for the same page
			if (!!this.groupPages &&
					!!this.groupPages[group.key] &&
					!!this.groupPages[group.key].pageNumber &&
					this.groupPages[group.key].pageNumber === group.currentPage) {
				return
			}

			this.groupPages[group.key] = {
				value: group.value,
				sortBy: this.groupPages[group.key]?.sortBy ?? this.defaultColumnSort,
				sortDesc: this.groupPages[group.key]?.sortDesc ?? false,
				sortDataType: this.groupPages[group.key]?.sortDataType ?? this.defaultSortDataType,
				pageNumber: group.currentPage ?? 1
			}

			const update: CurrentPageDetails = {
				value: group.value,
				pageNumber: group.currentPage,
				pageSize: this.perPage,
				searchTerm: this.searchFilter,
				sortBy: this.groupPages[group.key]?.sortBy,
				sortDesc: this.groupPages[group.key]?.sortDesc,
				sortDataType: this.groupPages[group.key]?.sortDataType,
				tableId: group.key ?? group.value
			}

			this.$emit("loadNextPage", update)
		}
	}

	@Emit("reloadInsightBySort")
	private setSortDetails (details: CurrentPageDetails): CurrentPageDetails {
		this.loadingPage = true

		const key = details.tableId ?? details.value

		this.groupPages[key] = {
			value: details.value,
			sortBy: details.sortBy,
			sortDesc: details.sortDesc,
			sortDataType: details.sortDataType,
			pageNumber: 1,
			tableId: details.tableId ? details.tableId : details.value
		}

		return {
			value: this.groupPages[key]?.value,
			pageNumber: this.groupPages[details.value]?.pageNumber ?? 1,
			pageSize: this.perPage,
			searchTerm: this.searchFilter,
			sortBy: this.groupPages[key]?.sortBy ?? this.defaultColumnSort,
			sortDesc: this.groupPages[key]?.sortDesc ?? false,
			sortDataType: this.groupPages[key]?.sortDataType,
			tableId: details.tableId ? details.tableId : details.value
		}
	}

	private updateBySearch () {
		if (this.debounceSearch) {
			clearTimeout(this.debounceSearch)
			this.debounceSearch = null
		}

		this.debounceSearch = setTimeout(() => {
			this.loadingPage = true
			Object.keys(this.groupPages).forEach(key => {
				if (!!this.groupPages && !!this.groupPages[key]) {
					this.groupPages[key].pageNumber = 1
					this.groupPages[key].sortBy = ""
					this.groupPages[key].sortDataType = DataType.none
					this.groupPages[key].sortDesc = false
				}
			})

			this.$emit("reloadInsightBySearch", this.searchFilter)
		}, 500)
	}

	private getSortColumn (key: string): string {
		return this.groupPages[key]?.sortBy ?? this.defaultColumnSort
	}

	private getSortDesc (key: string): boolean {
		return this.groupPages[key]?.sortDesc ?? false
	}

	private isTableLoading (group: any): boolean {
		const tableId = group.key ?? group.value
		return this.loadingPageTableIds.includes(tableId)
	}

	private totalRecordsAvailable (): number {
		return this.casesTables?.reduce((acc, current) => {
			return acc + current.totalRecords
		}, 0) ?? 0
	}

	private totalRecordsShown (): number {
		return this.casesTables?.reduce((acc, current) => {
			return acc + current.cases.length
		}, 0) ?? 0
	}

	private get totalRecordsText (): string {
		const records = this.totalRecordsAvailable()
		const recordString = records === 1 ? "record" : "records"

		return `${records} ${recordString}`
	}
}
